/**
 * Login Page
 */
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  TextField,
  Button,
  Typography,
  Alert,
  InputAdornment,
  IconButton,
} from '@mui/material';
import {
  Phone as PhoneIcon,
  Lock as LockIcon,
  Visibility,
  VisibilityOff,
} from '@mui/icons-material';
import { useLoginMutation } from '../../store/api/authApi';
import { useAppDispatch, useAppSelector } from '../../store/hooks';
import { setCredentials, logout } from '../../store/slices/authSlice';
import { palette } from '../../theme/palette';
import { getApiBaseUrl } from '../../utils/api';
import { parseApiError } from '../../utils/apiError';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
// import { LanguageSwitcher } from '../../components/common';

const LoginPage = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const { language } = useAppSelector((state) => state.ui);
  const [login, { isLoading }] = useLoginMutation();
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [formData, setFormData] = useState({
    phone_number: '',
    password: '',
  });

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
    setError(null);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    // Validation
    const phoneNumber = formData.phone_number.trim();
    if (!phoneNumber) {
      setError(t('auth.login.invalidCredentials'));
      return;
    }

    if (!formData.password) {
      setError(t('auth.login.passwordRequired'));
      return;
    }

    try {
      // Step 1: Prepare login data
      const loginData: { phone_number: string; password: string } = {
        phone_number: phoneNumber,
        password: formData.password,
      };

      // Step 2: Login to get tokens
      const loginResponse = await login(loginData).unwrap();

      // Step 3: Fetch user info using the access token
      const baseUrl = getApiBaseUrl();
      const userResponse = await fetch(`${baseUrl}/users/profile/`, {
        headers: {
          'Authorization': `Bearer ${loginResponse.access}`,
        },
      });

      if (!userResponse.ok) {
        throw new Error('Failed to fetch user information');
      }

      const userResponseData = await userResponse.json();

      // Handle response structure: { success: true, data: {...} } or direct user object
      const userData = userResponseData.data || userResponseData;

      // Step 4: Check if user is staff or superuser
      if (!userData.is_staff && !userData.is_superuser) {
        dispatch(logout());
        setError(t('auth.login.accessDenied'));
        return;
      }

      // Step 5: Set full credentials
      dispatch(
        setCredentials({
          user: userData,
          token: loginResponse.access,
          refreshToken: loginResponse.refresh,
        })
      );

      toast.success(t('common.success'));

      // Step 6: Redirect based on permissions
      // Get user permissions
      const permissions = userData.employee_profile?.permissions || [];
      const isSuperUser = userData.is_superuser;

      // Helper function to check permission
      const hasPermission = (permission: string) => {
        return isSuperUser || permissions.includes(permission);
      };

      // Check if employee has NO permissions at all
      if (!isSuperUser && permissions.length === 0) {
        navigate('/no-permissions');
        return;
      }

      // Determine first accessible page based on permission hierarchy
      let redirectPath = '/';  // Default to dashboard

      if (hasPermission('view_dashboard')) {
        redirectPath = '/';  // Dashboard
      } else if (hasPermission('view_orders')) {
        redirectPath = '/orders';  // Orders page
      } else if (hasPermission('view_payments')) {
        redirectPath = '/payments';  // Payments page
      } else if (hasPermission('view_services')) {
        redirectPath = '/services';  // Services page
      } else if (hasPermission('view_patients')) {
        redirectPath = '/users';  // Patients (accounts) page
      } else if (hasPermission('view_specialists')) {
        redirectPath = '/specialists';  // Specialists page
      } else if (hasPermission('view_consultants')) {
        redirectPath = '/consultants';  // Consultants page
      } else if (hasPermission('view_ads')) {
        redirectPath = '/ads';  // Ads page
      } else if (hasPermission('view_reports')) {
        redirectPath = '/reports';  // Reports page
      } else if (hasPermission('view_settings')) {
        redirectPath = '/settings';  // Settings page
      } else if (hasPermission('view_roles')) {
        redirectPath = '/roles';  // Roles page
      } else if (hasPermission('view_employees')) {
        redirectPath = '/employees';  // Employees page
      } else {
        // Has some permissions but none of the main view permissions
        navigate('/no-permissions');
        return;
      }

      navigate(redirectPath);
    } catch (err: unknown) {
      console.error('Login error:', err);
      setError(parseApiError(err, t('auth.login.invalidCredentials')));

      // Clear any stored tokens on error
      localStorage.removeItem('token');
      localStorage.removeItem('refreshToken');
    }
  };

  return (
    <Box sx={{ width: '100%', height: '100%', position: 'relative' }}>
      {/* Language Switcher - Top Right (or Top Left for RTL) */}
      {/* <AppBar
        position="absolute"
        elevation={0}
        sx={{
          backgroundColor: 'transparent',
          top: 0,
          right: language === 'ar' ? 'auto' : 0,
          left: language === 'ar' ? 0 : 'auto',
          width: 'auto',
        }}
      >
        <Toolbar sx={{ minHeight: '48px !important', p: '8px !important' }}>
          <LanguageSwitcher />
        </Toolbar>
      </AppBar> */}

      <Card
        elevation={8}
        sx={{
          width: '100%',
          borderRadius: { xs: 2, sm: 3 },
          overflow: 'hidden',
        }}
      >
        <CardContent sx={{ p: { xs: 3, sm: 4, md: 5 } }}>
          {/* Logo */}
          <Box sx={{ textAlign: 'center', mb: 4 }}>
            <Box
              component="img"
              src="/logo.jpg"
              alt={t('auth.login.title')}
              sx={{ height: 80, width: 'auto', display: 'block', mx: 'auto' }}
            />
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              {t('auth.login.subtitle')}
            </Typography>
          </Box>

          {error && (
            <Alert severity="error" sx={{ mb: 3 }}>
              {error}
            </Alert>
          )}

          <form onSubmit={handleSubmit}>
            <TextField
              fullWidth
              name="phone_number"
              label={t('auth.login.phone')}
              type="text"
              value={formData.phone_number}
              onChange={handleChange}
              required
              placeholder={language === 'ar' ? 'رقم الهاتف' : 'Phone Number'}
              dir={language === 'ar' ? 'rtl' : 'ltr'}
              sx={{
                mb: 2,
                '& .MuiInputBase-input': {
                  textAlign: language === 'ar' ? 'right' : 'left',
                },
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <PhoneIcon color="action" />
                  </InputAdornment>
                ),
              }}
            />

            <TextField
              fullWidth
              name="password"
              label={t('auth.login.password')}
              type={showPassword ? 'text' : 'password'}
              value={formData.password}
              onChange={handleChange}
              required
              dir={language === 'ar' ? 'rtl' : 'ltr'}
              sx={{
                mb: 3,
                '& .MuiInputBase-input': {
                  textAlign: language === 'ar' ? 'right' : 'left',
                },
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment
                    position="start"
                    sx={{
                      ...(language === 'ar' && {
                        marginRight: '0 !important',
                        paddingRight: 0,
                        '& .MuiSvgIcon-root': {
                          marginRight: '0 !important',
                        }
                      })
                    }}
                  >
                    <LockIcon color="action" />
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end" sx={{ ml: language === 'ar' ? 0 : undefined }}>
                    <IconButton
                      onClick={() => setShowPassword(!showPassword)}
                      edge="end"
                      aria-label={showPassword ? 'Hide password' : 'Show password'}
                      size="small"
                    >
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />

            <Box sx={{ textAlign: 'left', mb: 2 }}>
              <Typography
                component="a"
                href="/forgot-password"
                variant="body2"
                sx={{
                  color: palette.brandSecondary[500],
                  textDecoration: 'none',
                  cursor: 'pointer',
                  '&:hover': {
                    textDecoration: 'underline',
                    color: palette.brandSecondary[600],
                  },
                }}
                onClick={(e: React.MouseEvent) => {
                  e.preventDefault();
                  navigate('/forgot-password');
                }}
              >
                {t('auth.login.forgotPassword')}
              </Typography>
            </Box>

            <Button
              type="submit"
              fullWidth
              variant="contained"
              size="large"
              disabled={isLoading}
              sx={{
                py: 1.5,
                background: `linear-gradient(180deg, ${palette.brand[500]} 0%, ${palette.brandSecondary[500]} 100%)`,
                '&:hover': {
                  background: `linear-gradient(180deg, ${palette.brand[600]} 0%, ${palette.brandSecondary[600]} 100%)`,
                },
              }}
            >
              {isLoading ? t('auth.login.signingIn') : t('auth.login.signIn')}
            </Button>
          </form>
        </CardContent>
      </Card>
    </Box>
  );
};

export default LoginPage;
